import {NgModule} from '@angular/core';
import {FormsModule} from '@angular/forms';

import {ButtonModule} from 'primeng/button';
import {ChipModule} from 'primeng/chip';
import {DatePickerModule} from 'primeng/datepicker';
import {DialogModule} from 'primeng/dialog';
import {DividerModule} from 'primeng/divider';
import {InputTextModule} from 'primeng/inputtext';
import {MessageModule} from 'primeng/message';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {SelectModule} from 'primeng/select';
import {TableModule} from 'primeng/table';
import {TagModule} from 'primeng/tag';
import {TooltipModule} from 'primeng/tooltip';

import {SmNoticeComponent} from './component/sm-notice/sm-notice.component';
import {SmSignaturePadComponent} from './component/sm-signature-pad/sm-signature-pad.component';
import {SmAdHocSlotComponent} from './component/sm-ad-hoc-slot/sm-ad-hoc-slot.component';
import {
    SmSignatureFieldListComponent
} from './component/sm-signature-field-list/sm-signature-field-list.component';
import {
    SmSignaturePdfGuidedComponent
} from './component/sm-signature-pdf-guided/sm-signature-pdf-guided.component';
import {
    SmSigningSessionComponent
} from './component/sm-signing-session/sm-signing-session.component';

/**
 * The guided signing surface as a self-contained, embeddable module.
 *
 * <h3>Why this module exists</h3>
 * The signature capture surface is about to gain a SECOND consumer: an external customer signing
 * application, separate from the internal shell. Copying the components into it is the one change
 * in the whole undertaking whose failure mode is silent — a geometry error puts the signature
 * marker in the wrong place and looks exactly as finished as a correct one. So the code is shared,
 * not duplicated: one implementation, two consumers.
 *
 * What stood in the way was never the code, it was the module cut. Every component here used to be
 * declared in {@code SmModule}, which imports the whole {@code CoreModule} — CcModule, UiModule,
 * SgModule, XuModule, VtModule, SharedModule and {@code CoreRoutingModule} with its route table.
 * A second application would have inherited all of it to render a PDF and a signature pad. This
 * module declares the same classes against their ACTUAL dependencies instead: {@code FormsModule}
 * for the two-way bindings of the capture form, and the twelve PrimeNG entry points the four
 * templates really use. No CoreModule, no router, no entity registry, no test providers.
 *
 * <h3>Why these five components and no others</h3>
 * They form a closed graph, verified against the templates:
 * <ul>
 *   <li>{@code mvs-sm-signing-session} renders exactly one of the two presentations and provides
 *       the {@code SmSigningSessionService} at component level, so every embedding gets its own
 *       session;</li>
 *   <li>{@code mvs-sm-signature-pdf-guided} (the guided view) and {@code mvs-sm-signature-field-list}
 *       (the flat card list) are those two presentations;</li>
 *   <li>both of them capture through {@code mvs-sm-ad-hoc-slot}, which in turn renders one
 *       {@code mvs-sm-signature-pad} per document field.</li>
 * </ul>
 * Nothing in the graph reaches outside it for a component, directive or pipe. The remaining sm
 * components — the ad-hoc findings block, the stamp result, the CRUD and object components, every
 * page — stay in {@code SmModule}: they are internal-shell surface and none of them is reachable
 * from the five above.
 *
 * <h3>What a consuming application still has to bring</h3>
 * These are genuine dependencies of the code, not of the module cut, and they do not dissolve by
 * being declared elsewhere:
 * <ul>
 *   <li>the npm packages {@code pdfjs-dist} and {@code signature_pad};</li>
 *   <li>the pdf.js worker as a served asset — the guided view resolves it through
 *       {@code pdfWorkerSrc()} in {@code features/core/shared/helper/pdfjs-worker.helper.ts},
 *       which expects {@code assets/pdf.worker.min.mjs} to exist relative to the base href. The
 *       asset glob and the {@code .mjs} JavaScript MIME type have to be configured per target;</li>
 *   <li>PrimeNG theming — the components are styled entirely through PrimeNG and PrimeFlex
 *       utility classes, so an application without that stylesheet renders them unstyled. Note that
 *       PrimeIcons is NOT among the dependencies of these five components: they render no
 *       {@code pi pi-*} glyph at all. See the next item for the font they do use;</li>
 *   <li><b>Font Awesome, and specifically the PRO set.</b> The four templates render twenty distinct
 *       {@code fa-solid}/{@code fa-regular} glyphs — the signature pad's "Hier unterschreiben", the
 *       re-entry confirmation, the whole zoom and paging bar — and this module loads nothing for
 *       them. {@code shell-alpha} supplies them from a kit in its own {@code index.html}, and that
 *       kit reports {@code "license":"pro"}. That is not incidental: measured against
 *       {@code @fortawesome/fontawesome-free} 6.7.2, only ELEVEN of the twenty resolve. One name
 *       ({@code fa-location-question}, the "not locatable in the document" heading) does not exist
 *       in the free set at all, and eight more are written {@code fa-regular} while the free Regular
 *       face carries just 163 glyphs and none of those eight — {@code minus}, {@code eraser},
 *       {@code pen-fancy}, {@code chevron-left}, {@code chevron-right},
 *       {@code magnifying-glass-minus}, {@code magnifying-glass-plus},
 *       {@code arrows-left-right-to-line}. So a consumer that reaches for the free package to
 *       satisfy this line has not met the dependency, it has halved the outage into something
 *       nobody looks for again. Either license the same Pro set, or change these call sites — and
 *       changing them changes the internal application too, because it is these very files.</li>
 *   <li>A consumer that brings NEITHER is not merely unstyled: it renders the signature, zoom and
 *       paging controls as empty space, with no error, no warning and no failing build. Checked by
 *       opening the guided view, not by compiling.</li>
 * </ul>
 * The transport of the captured signatures is NOT part of this module. The five components emit
 * their captures and leave sending them to the host, which is what lets an external application
 * post them to a different endpoint than the internal shell does.
 *
 * <h3>Declaration ownership</h3>
 * A component may be declared in exactly ONE NgModule. {@code SmModule} imports and re-exports
 * this module and must therefore NOT declare these five classes itself.
 */
@NgModule({
    declarations: [
        SmSignaturePadComponent,
        SmAdHocSlotComponent,
        SmSignatureFieldListComponent,
        SmSignaturePdfGuidedComponent,
        SmSigningSessionComponent,
        // The SIXTH, and declared here rather than in SmModule because the guided view itself renders
        // one: the capture dialog explains what an unconfirmed drawing is, and that explanation is
        // part of the closed graph above. It carries no endpoint, no vocabulary and no state of its
        // own — it is a way of setting a sentence — so it is safe for both consumers by construction.
        // @ticket TM-1000002003
        SmNoticeComponent,
    ],
    exports: [
        SmNoticeComponent,
        SmSignaturePadComponent,
        SmAdHocSlotComponent,
        SmSignatureFieldListComponent,
        SmSignaturePdfGuidedComponent,
        SmSigningSessionComponent,
    ],
    imports: [
        FormsModule,
        ButtonModule,
        ChipModule,
        DatePickerModule,
        DialogModule,
        DividerModule,
        InputTextModule,
        MessageModule,
        ProgressSpinnerModule,
        SelectModule,
        TableModule,
        TagModule,
        TooltipModule,
    ]
})
export class SmGuidedSigningModule {
}
